/**
 * The sealed room, client side. Keys live only in this browser; the board orders
 * MLS messages and stores ciphertext it can't read. See docs/protocol.md, "The vault".
 */
import {
  acceptAll, createApplicationMessage, createCommit, createGroup, decodeGroupState, decodeMlsMessage,
  defaultCapabilities, defaultLifetime, emptyPskIndex, encodeGroupState, encodeMlsMessage, generateKeyPackage,
  getCiphersuiteFromName, getCiphersuiteImpl, joinGroup, mlsExporter, processMessage,
  type CiphersuiteImpl, type ClientState, type KeyPackage, type MLSMessage, type PrivateKeyPackage,
} from "ts-mls";
import { defaultClientConfig } from "ts-mls/clientConfig.js";
import type { ChatLine, Ctx, ServerEvent, VaultEntry, VaultEvent, VaultOp, Vars } from "../shared/protocol";
import type { Renderer } from "./render";

const SUITE = "MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519";
const HISTORY = 50;
const enc = new TextEncoder();
const dec = new TextDecoder();

const b64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const wire = (m: MLSMessage) => b64(encodeMlsMessage(m));
const unwire = (s: string) => decodeMlsMessage(unb64(s), 0)![0];

/** What this device remembers about the vault. Device-local, like Signal: lose the device, lose this. */
interface Stored {
  /** Key package waiting at the door for a key. */
  keys?: { pub: string; priv: { init: string; hpke: string; sig: string } };
  state?: string;
  /** A commit this device sent and the board hasn't confirmed: the state to adopt if it lands. */
  pending?: { epoch: number; state: string };
  /** The last log entry this device processed. */
  seq: number;
  history: ChatLine[];
  /** This device's own lines by log position: MLS senders can't decrypt their own messages. */
  sent: Record<number, { kind: "say" | "me"; text: string }>;
}

export interface VaultLink {
  /** Send a vault op and wait for the board's reply. */
  request(op: VaultOp): Promise<ServerEvent[]>;
  /** Print lines above the prompt. */
  print(ansi: string): void;
  renderer: Renderer;
  ctx(): Ctx;
  /** Where this device keeps its keys. The browser's localStorage. */
  storage: Pick<Storage, "getItem" | "setItem">;
}

type Reply = Extract<VaultEvent, { op: "accepted" }> | Extract<VaultEvent, { op: "rejected" }>;
const replyOf = (events: ServerEvent[]) =>
  events.find((e): e is Reply & ServerEvent => e.t === "vault" && (e.op === "accepted" || e.op === "rejected"));

export class VaultSession {
  private cs!: CiphersuiteImpl;
  private state?: ClientState;
  private store: Stored;
  private members: string[] = [];
  private waiting: string[] = [];
  private sysop = false;
  private warned = false;
  /** Serializes every change to MLS state. Network waits happen outside it. */
  private queue: Promise<unknown> = Promise.resolve();

  constructor(private link: VaultLink, private storageKey: string, private handle: string) {
    this.store = this.load();
  }

  private run<T>(job: () => Promise<T>): Promise<T> {
    const next = this.queue.then(job, job);
    this.queue = next.catch((err) => console.error("vault", err));
    return next;
  }

  // ── Storage ────────────────────────────────────────────────────────────

  private load(): Stored {
    try {
      const raw = this.link.storage.getItem(this.storageKey);
      if (raw) return JSON.parse(raw) as Stored;
    } catch {
      // Storage blocked or corrupt: this device starts without a key.
    }
    return { seq: 0, history: [], sent: {} };
  }

  private save() {
    if (this.state) this.store.state = b64(encodeGroupState(this.state));
    else delete this.store.state;
    this.store.history = this.store.history.slice(-HISTORY);
    const keep = Object.keys(this.store.sent).map(Number).sort((a, b) => b - a).slice(0, HISTORY);
    this.store.sent = Object.fromEntries(keep.map((k) => [k, this.store.sent[k]]));
    try {
      this.link.storage.setItem(this.storageKey, JSON.stringify(this.store));
    } catch {
      this.say("vaultBroken");
    }
  }

  private restore(s: string): ClientState {
    const [group] = decodeGroupState(unb64(s), 0)!;
    return { ...group, clientConfig: defaultClientConfig };
  }

  // ── Output ─────────────────────────────────────────────────────────────

  private say(key: string, vars?: Vars) {
    this.link.print(this.link.renderer.text(key, this.link.ctx(), vars) + "\x1b[0m\r\n");
  }

  private show(line: ChatLine, remember = true) {
    this.link.print(this.link.renderer.chatLine(this.link.ctx(), line));
    if (remember) this.store.history.push(line);
  }

  // ── Walking in ─────────────────────────────────────────────────────────

  enter(): Promise<void> {
    return this.run(async () => {
      this.cs ??= await getCiphersuiteImpl(getCiphersuiteFromName(SUITE));
      this.state = this.store.state ? this.restore(this.store.state) : undefined;
      const st = await this.fetchState();
      if (!st) return;
      this.sysop = st.sysop;
      this.members = st.members;
      this.waiting = st.waiting;
      if (!this.warned) this.say("vaultAudit");
      this.warned = true;

      if (!st.exists) {
        if (st.sysop) return this.open();
        return this.atDoor(st.hasKeyPackage);
      }
      if (this.state && !st.member) {
        this.forget();
        this.say("vaultRemovedYou");
        return this.atDoor(st.hasKeyPackage);
      }
      if (!this.state && st.member) {
        if (st.welcome && this.store.keys) {
          await this.join(st.welcome);
        } else {
          this.say(st.sysop ? "vaultSysopLost" : "vaultLostDevice");
          if (!st.sysop) await this.leaveKeyPackage();
          return this.save();
        }
      }
      if (!this.state) return this.atDoor(st.hasKeyPackage);

      for (const line of this.store.history.slice(-20)) this.show(line, false);
      this.adoptPending(st.backlog, st.epoch);
      for (const entry of st.backlog) await this.process(entry);
      if (this.sysop && this.waiting.length) this.say("vaultWaiting", { who: this.waiting.join(", ") });
      this.save();
    });
  }

  private async fetchState() {
    const events = await this.link.request({ op: "enter", since: this.store.seq, hasState: !!this.state });
    return events.find((e): e is Extract<VaultEvent, { op: "state" }> & ServerEvent => e.t === "vault" && e.op === "state");
  }

  /** The sysop's first visit: make the group, holding the only key. */
  private async open() {
    const keys = await this.makeKeys();
    this.state = await createGroup(enc.encode(`atdt:${this.storageKey}`), keys.publicPackage, keys.privatePackage, [], this.cs);
    const reply = replyOf(await this.link.request({ op: "create" }));
    if (reply?.op !== "accepted") return this.say("vaultBusy");
    this.store.seq = reply.seq;
    this.members = [this.handle];
    this.say("vaultOpening");
    this.save();
  }

  /** Drop every key and the whole log, then open the vault fresh with this device holding the only key. */
  private async reopen() {
    const reply = replyOf(await this.link.request({ op: "reopen" }));
    if (reply?.op !== "accepted") return this.say("vaultBusy");
    this.forget();
    this.store.seq = 0;
    await this.open();
  }

  private async atDoor(boardHasPackage: boolean) {
    if (!boardHasPackage || !this.store.keys) await this.leaveKeyPackage();
    this.say("vaultNoKey");
    this.save();
  }

  private async join(welcome: { bytes: string; seq: number }) {
    const keys = this.store.keys!;
    const pub = (unwire(keys.pub) as { keyPackage: KeyPackage }).keyPackage;
    const priv: PrivateKeyPackage = {
      initPrivateKey: unb64(keys.priv.init), hpkePrivateKey: unb64(keys.priv.hpke), signaturePrivateKey: unb64(keys.priv.sig),
    };
    const msg = unwire(welcome.bytes);
    if (msg.wireformat !== "mls_welcome") return this.say("vaultBroken");
    this.state = await joinGroup(msg.welcome, pub, priv, emptyPskIndex, this.cs);
    // Key packages are single-use. A future device key needs a fresh one.
    delete this.store.keys;
    this.store.seq = welcome.seq;
    this.store.history = [];
    this.store.sent = {};
    this.say("vaultJoined", { by: this.link.renderer.sysop });
    this.save();
  }

  private async makeKeys() {
    return generateKeyPackage(
      { credentialType: "basic", identity: enc.encode(this.handle) }, defaultCapabilities(), defaultLifetime, [], this.cs,
    );
  }

  /** Leave a public key package at the door so the sysop can hand this device a key. */
  private async leaveKeyPackage() {
    const keys = await this.makeKeys();
    const pub = wire({ keyPackage: keys.publicPackage, wireformat: "mls_key_package", version: "mls10" });
    const p = keys.privatePackage;
    this.store.keys = { pub, priv: { init: b64(p.initPrivateKey), hpke: b64(p.hpkePrivateKey), sig: b64(p.signaturePrivateKey) } };
    this.save();
    await this.link.request({ op: "keypackage", bytes: pub });
  }

  private forget() {
    this.state = undefined;
    delete this.store.pending;
    this.store.history = [];
    this.store.sent = {};
    this.save();
  }

  /** A commit this device sent before a reload, which the board went on to accept. */
  private adoptPending(backlog: VaultEntry[], epoch: number) {
    const pending = this.store.pending;
    if (!pending) return;
    delete this.store.pending;
    const ours = backlog.find((e) => e.kind === "commit" && e.epoch === pending.epoch && e.sender === this.handle);
    if (ours && epoch > pending.epoch) {
      this.state = this.restore(pending.state);
      this.store.seq = ours.seq;
    }
  }

  // ── The log ────────────────────────────────────────────────────────────

  /** Something new in the room, pushed by the board. */
  onPush(e: VaultEvent): Promise<void> {
    return this.run(async () => {
      if (e.op === "knocking") {
        if (!this.waiting.includes(e.handle)) this.waiting.push(e.handle);
        this.say("vaultKnocking", { who: e.handle });
        return;
      }
      if (e.op !== "entry") return;
      if (!this.state) {
        // At the door: the only entry that matters is the one handing this device a key.
        if (e.entry.added?.toLowerCase() === this.handle.toLowerCase()) setTimeout(() => this.enter(), 0);
        return;
      }
      await this.process(e.entry);
      this.save();
    });
  }

  private async process(entry: VaultEntry) {
    if (!this.state || entry.seq <= this.store.seq) return;
    const mine = entry.sender.toLowerCase() === this.handle.toLowerCase();

    if (entry.kind === "commit") {
      if (mine) {
        // Our own commit from another tab or a lost reply: we can't apply it, so we're out of step.
        this.store.seq = entry.seq;
        this.forget();
        return this.say("vaultBroken");
      }
      try {
        const msg = unwire(entry.bytes);
        if (msg.wireformat !== "mls_private_message" && msg.wireformat !== "mls_public_message") throw new Error("not a commit");
        const result = await processMessage(msg, this.state, emptyPskIndex, acceptAll, this.cs);
        this.state = result.newState;
      } catch (err) {
        console.error("vault commit", err);
        this.store.seq = entry.seq;
        this.forget();
        return this.say("vaultBroken");
      }
      this.store.seq = entry.seq;
      this.noteMembership(entry);
      if (this.state.groupActiveState.kind === "removedFromGroup") {
        this.forget();
        this.say("vaultRemovedYou");
      }
      return;
    }

    this.store.seq = entry.seq;
    if (mine) {
      const own = this.store.sent[entry.seq];
      return this.show({ kind: own?.kind ?? "say", handle: this.handle, text: own?.text ?? "", at: entry.at });
    }
    try {
      const msg = unwire(entry.bytes);
      if (msg.wireformat !== "mls_private_message") throw new Error("not a message");
      const result = await processMessage(msg, this.state, emptyPskIndex, acceptAll, this.cs);
      this.state = result.newState;
      if (result.kind !== "applicationMessage") throw new Error("not an application message");
      const body = JSON.parse(dec.decode(result.message)) as { kind: "say" | "me"; text: string };
      this.show({ kind: body.kind === "me" ? "me" : "say", handle: entry.sender, text: String(body.text ?? ""), at: entry.at });
    } catch (err) {
      console.error("vault message", err);
      this.say("vaultCantRead");
    }
  }

  private noteMembership(entry: VaultEntry) {
    if (entry.removed) this.members = this.members.filter((m) => m.toLowerCase() !== entry.removed!.toLowerCase());
    if (entry.added && !this.members.some((m) => m.toLowerCase() === entry.added!.toLowerCase())) this.members.push(entry.added);
    if (entry.added) this.waiting = this.waiting.filter((w) => w.toLowerCase() !== entry.added!.toLowerCase());
    if (entry.added && entry.removed) this.say("vaultHanded", { by: entry.sender, who: entry.added });
    else if (entry.added) this.say("vaultHanded", { by: entry.sender, who: entry.added });
    else if (entry.removed) this.say("vaultTaken", { by: entry.sender, who: entry.removed });
  }

  // ── Typing ─────────────────────────────────────────────────────────────

  /** A line typed in the vault. Returns true when the caller asked to leave. */
  async line(raw: string): Promise<boolean> {
    const line = raw.trim();
    const [command, ...rest] = line.split(" ");
    const arg = rest.join(" ").trim();
    if (!line) return false;

    if (command === "/q" || command === "/quit" || command === "/leave") {
      await this.queue;
      await this.link.request({ op: "leave" });
      return true;
    }
    if (command === "/help") {
      this.say("vaultHelp", { opsHelp: "" });
      if (this.sysop) this.say("vaultOpsHelp");
      return false;
    }
    if (command === "/who") {
      this.say("vaultMembers", { who: this.members.join(", ") });
      return false;
    }
    if (command === "/knock") {
      await this.run(() => this.knock());
      return false;
    }
    if (command === "/door") {
      if (!this.sysop) this.say("vaultNotSysop");
      else if (this.waiting.length) this.say("vaultWaiting", { who: this.waiting.join(", ") });
      else this.say("vaultNobodyWaiting");
      return false;
    }
    if (command === "/reopen") {
      if (!this.sysop) this.say("vaultNotSysop");
      else if (this.state && arg !== "yes") this.say("vaultReopenConfirm");
      else await this.run(() => this.reopen());
      return false;
    }
    if (command === "/key" || command === "/unkey") {
      if (!this.sysop) this.say("vaultNotSysop");
      else if (!arg) this.say("vaultUnknown");
      else await this.membership(arg, command === "/key");
      return false;
    }
    if (line.startsWith("/") && command !== "/me") {
      this.say("vaultUnknown");
      return false;
    }
    if (!this.state) {
      this.say(this.store.keys ? "vaultNoKey" : "vaultNoKeyHere");
      return false;
    }
    const body = command === "/me" ? { kind: "me" as const, text: arg } : { kind: "say" as const, text: line };
    if (body.text) await this.send(body);
    return false;
  }

  /** The code both sides compare in person. Different codes mean different keys: someone is in the middle. */
  private async knock() {
    if (!this.state) return this.say("vaultNoKey");
    const bytes = await mlsExporter(this.state.keySchedule.exporterSecret, "atdt secret knock", new Uint8Array(), 4, this.cs);
    const code = [...bytes].map((b) => String(b).padStart(3, "0")).join("-");
    this.say("vaultKnock", { code, epoch: Number(this.state.groupContext.epoch) });
  }

  private async send(body: { kind: "say" | "me"; text: string }) {
    for (let attempt = 0; attempt < 3; attempt++) {
      const built = await this.run(async () => {
        if (!this.state) return undefined;
        const r = await createApplicationMessage(this.state, enc.encode(JSON.stringify(body)), this.cs);
        // Keep the advanced ratchet even if the board says no: reusing a generation would reuse a key.
        this.state = r.newState;
        this.save();
        return {
          epoch: Number(r.newState.groupContext.epoch),
          bytes: wire({ privateMessage: r.privateMessage, wireformat: "mls_private_message", version: "mls10" }),
        };
      });
      if (!built) return this.say("vaultNoKey");
      const reply = replyOf(await this.link.request({ op: "send", epoch: built.epoch, bytes: built.bytes }));
      if (reply?.op === "accepted") {
        return this.run(async () => {
          this.store.sent[reply.seq] = body;
          await this.process({ seq: reply.seq, epoch: built.epoch, kind: "message", sender: this.handle, bytes: "", at: Date.now() });
          this.save();
        });
      }
      if (reply?.reason !== "stale") return this.say(reply?.reason === "notMember" ? "vaultNoKey" : "vaultBusy");
      // The room re-keyed under us. Catch up on what we missed, then try again in the new epoch.
      await this.catchUp();
    }
    this.say("vaultBusy");
  }

  private async catchUp() {
    const events = await this.link.request({ op: "enter", since: this.store.seq, hasState: !!this.state });
    const st = events.find((e): e is Extract<VaultEvent, { op: "state" }> & ServerEvent => e.t === "vault" && e.op === "state");
    if (!st) return;
    await this.run(async () => {
      this.waiting = st.waiting;
      for (const entry of st.backlog) await this.process(entry);
      this.save();
    });
  }

  /** Hand a key to `who` (replacing any old device of theirs), or take theirs back. */
  private async membership(who: string, handing: boolean) {
    let pkg: KeyPackage | undefined;
    let target = who;
    if (handing) {
      const events = await this.link.request({ op: "fetchKeyPackage", handle: who });
      const found = events.find((e): e is Extract<VaultEvent, { op: "keyPackage" }> & ServerEvent => e.t === "vault" && e.op === "keyPackage");
      if (!found?.bytes) return this.say("vaultNoPackage", { who });
      const msg = unwire(found.bytes);
      if (msg.wireformat !== "mls_key_package") return this.say("vaultBroken");
      // The board says whose package this is; the package itself has to agree.
      const identity = dec.decode((msg.keyPackage.leafNode.credential as { identity: Uint8Array }).identity);
      if (identity.toLowerCase() !== found.handle.toLowerCase()) return this.say("vaultBroken");
      pkg = msg.keyPackage;
      target = found.handle;
    }

    for (let attempt = 0; attempt < 3; attempt++) {
      const built = await this.run(async () => {
        if (!this.state) return undefined;
        const leaf = this.leafOf(target);
        if (!handing && leaf === undefined) return "notMember" as const;
        const proposals = [];
        if (leaf !== undefined) proposals.push({ proposalType: "remove" as const, remove: { removed: leaf } });
        if (pkg) proposals.push({ proposalType: "add" as const, add: { keyPackage: pkg } });
        const epoch = Number(this.state.groupContext.epoch);
        const r = await createCommit({ state: this.state, cipherSuite: this.cs }, { extraProposals: proposals, ratchetTreeExtension: true });
        this.store.pending = { epoch, state: b64(encodeGroupState(r.newState)) };
        this.save();
        return {
          epoch, next: r.newState, replaced: leaf !== undefined,
          bytes: wire(r.commit),
          welcome: r.welcome ? wire({ welcome: r.welcome, wireformat: "mls_welcome", version: "mls10" }) : undefined,
        };
      });
      if (!built) return this.say("vaultNoKey");
      if (built === "notMember") return this.say("vaultNotMember", { who });

      const reply = replyOf(await this.link.request({
        op: "commit", epoch: built.epoch, bytes: built.bytes, welcome: built.welcome,
        added: handing ? target : undefined, removed: built.replaced ? target : undefined,
      }));
      if (reply?.op === "accepted") {
        return this.run(async () => {
          delete this.store.pending;
          this.state = built.next;
          this.store.seq = reply.seq;
          this.noteMembership({
            seq: reply.seq, epoch: built.epoch, kind: "commit", sender: this.handle, bytes: "", at: Date.now(),
            added: handing ? target : undefined, removed: !handing ? target : undefined,
          });
          this.save();
        });
      }
      await this.run(async () => {
        delete this.store.pending;
        this.save();
      });
      if (reply?.reason !== "stale") return this.say(reply?.reason === "noKeyPackage" ? "vaultNoPackage" : "vaultBusy", { who });
      await this.catchUp();
    }
    this.say("vaultBusy");
  }

  /** The leaf index of `who`'s device in the room, if they hold a key. */
  private leafOf(who: string): number | undefined {
    const tree = this.state!.ratchetTree;
    for (let i = 0; i < tree.length; i += 2) {
      const node = tree[i];
      if (node?.nodeType !== "leaf") continue;
      const identity = dec.decode((node.leaf.credential as { identity: Uint8Array }).identity);
      if (identity.toLowerCase() === who.toLowerCase()) return i / 2;
    }
    return undefined;
  }
}
